import Characteristic from './Characteristic.js';

const PRICE_TYPE_RECURRING = 'recurring';
const PRICE_TYPE_ONE_TIME = 'punctual';
const PRICE_PERIOD_MONTHLY = 'month';
const PRODUCT_SPECIFICATION_ID_CHANGE_TO_E_SIM = 'changeESIM';
const PRODUCT_SPECIFICATION_ID_CHANGE_TO_PHYSICAL_SIM = 'rollbackToPreviousSIM';

export default class Product {
  constructor(data) {
    this._data = data;
  }

  /**
   * @return {string}
   */
  get id() {
    return this._data.id;
  }

  /**
   * @return {string}
   */
  get name() {
    return this._data.name;
  }

  /**
   * @return {string}
   */
  get status() {
    return this._data.status;
  }

  /**
   * @return {?string}
   */
  get productSpecificationId() {
    return this._data.productSpecification?.id ?? null;
  }

  /**
   * @return {Characteristic[]}
   */
  get characteristics() {
    return this._data.characteristic?.map(item => new Characteristic(item)) ?? [];
  }

  /**
   * @return {?number}
   */
  get activationPrice() {
    return this._getPriceByPredicate(item => item.priceType === PRICE_TYPE_ONE_TIME);
  }

  /**
   * @return {?number}
   */
  get monthlyPrice() {
    return this._getPriceByPredicate(
      item =>
        item.priceType === PRICE_TYPE_RECURRING &&
        item.recurringChargePeriod === PRICE_PERIOD_MONTHLY,
    );
  }

  /**
   * @return {?number}
   */
  get changeESimPrice() {
    return this._getPriceByPredicate(
      item => item.name === PRODUCT_SPECIFICATION_ID_CHANGE_TO_E_SIM,
    );
  }

  /**
   * @return {?number}
   */
  get rollbackESimPrice() {
    return this._getPriceByPredicate(
      item => item.name === PRODUCT_SPECIFICATION_ID_CHANGE_TO_PHYSICAL_SIM,
    );
  }

  /**
   * @return {object[]}
   * @private
   */
  get _prices() {
    const prices = this._data.productPrice ?? this._data.productOfferingPrice;

    if (!Array.isArray(prices) || prices.length === 0) {
      return [];
    }

    return prices;
  }

  /**
   * @return {boolean}
   */
  get isSimManagementAccessible() {
    return this.changeESimPrice !== null && this.rollbackESimPrice !== null;
  }

  /**
   * @param {function(*): boolean} predicate
   * @return {?Number}
   * @private
   */
  _getPriceByPredicate(predicate) {
    const priceItem = this._prices.find(predicate);

    const price = priceItem?.price?.taxIncludedAmount?.value;
    if (!price) {
      return null;
    }

    return Number.parseFloat(price);
  }

  /**
   * @param {string} name
   * @return {?Characteristic}
   */
  getCharacteristicByName(name) {
    return this.characteristics.find(characteristic => characteristic.name === name) ?? null;
  }

  /**
   * @param {string} name
   * @return {?any}
   */
  getCharacteristicValueByName(name) {
    return this.getCharacteristicByName(name)?.value ?? null;
  }

  /**
   * @param {string} name
   * @return {Characteristic[]}
   */
  getCharacteristicsByName(name) {
    return this.characteristics.filter(characteristic => characteristic.name === name);
  }
}
